import type { TerraApiRouteInput } from '@terra/shared-utils';
import type {
  FindingsContract,
  FindingListEnrichedDto,
  FindingsSortBy,
  FindingsThenSortBy,
} from '@terra/shared-contracts-findings';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { PaginationDefaults } from '@terra/shared-dto-types';
import {
  enrichFindingWithAssets,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import type { FindingsBulkFilter } from './types';

export type FindingsListEnrichedQuery = TerraApiRouteInput<
  typeof FindingsContract.listFindingsEnriched
>['query'];

export type FindingsListEnrichedQueryOptions = FindingsBulkFilter & {
  skip?: number;
  limit?: number;
  signalId?: string;
  findingIds?: string[];
  includeUnpublished?: boolean;
  sortBy?: FindingsSortBy;
  sortOrder?: 'asc' | 'desc';
  thenSortBy?: FindingsThenSortBy;
  thenSortOrder?: 'asc' | 'desc';
};

export const mapFindingListEnrichedToInternal = (
  finding: FindingListEnrichedDto,
): InternalEnrichedFinding => enrichFindingWithAssets(finding);

export const buildFindingsListEnrichedQueryForTarget = ({
  targetId,
  targetType,
  options,
}: {
  targetId: string;
  targetType: TargetTypeType;
  options?: FindingsListEnrichedQueryOptions;
}): FindingsListEnrichedQuery => {
  const trimmedSearch = options?.searchTerm?.trim();

  return {
    skip: options?.skip ?? 0,
    limit: options?.limit ?? PaginationDefaults.LIMIT,
    targetType: [targetType],
    targetId,
    includeUnpublished: options?.includeUnpublished ?? true,
    ...(options?.findingIds !== undefined
      ? { findingIds: options.findingIds }
      : {}),
    ...(options?.signalId ? { signalId: options.signalId } : {}),
    ...(options?.endpointId ? { endpointId: options.endpointId } : {}),
    ...(trimmedSearch ? { searchTerm: trimmedSearch } : {}),
    ...(options?.severity?.length ? { severity: options.severity } : {}),
    ...(options?.status?.length ? { status: options.status } : {}),
    ...(options?.category?.length ? { category: options.category } : {}),
    ...(options?.isPublished !== undefined
      ? { isPublished: options.isPublished }
      : {}),
    ...(options?.wasAutoPublished !== undefined
      ? { wasAutoPublished: options.wasAutoPublished }
      : {}),
    ...(options?.hasPrevention !== undefined
      ? { hasPrevention: options.hasPrevention }
      : {}),
    ...(options?.hasPendingSeverityChangeRequest !== undefined
      ? {
          hasPendingSeverityChangeRequest:
            options.hasPendingSeverityChangeRequest,
        }
      : {}),
    ...(options?.createdStartDate
      ? { createdStartDate: options.createdStartDate }
      : {}),
    ...(options?.createdEndDate
      ? { createdEndDate: options.createdEndDate }
      : {}),
    ...(options?.discoveryStartDate
      ? { discoveryStartDate: options.discoveryStartDate }
      : {}),
    ...(options?.discoveryEndDate
      ? { discoveryEndDate: options.discoveryEndDate }
      : {}),
    ...(options?.lifecycleState
      ? { lifecycleState: options.lifecycleState }
      : {}),
    ...(options?.sortBy ? { sortBy: options.sortBy } : {}),
    ...(options?.sortOrder ? { sortOrder: options.sortOrder } : {}),
    ...(options?.thenSortBy ? { thenSortBy: options.thenSortBy } : {}),
    ...(options?.thenSortOrder ? { thenSortOrder: options.thenSortOrder } : {}),
  };
};

export const buildFindingsListNavigationQueryForTarget = ({
  targetId,
  targetType,
  options,
}: {
  targetId: string;
  targetType: TargetTypeType;
  options?: Omit<
    FindingsListEnrichedQueryOptions,
    'skip' | 'limit' | 'findingIds'
  >;
}): FindingsListEnrichedQuery =>
  buildFindingsListEnrichedQueryForTarget({
    targetId,
    targetType,
    options: {
      ...options,
      skip: 0,
      limit: 1,
    },
  });

export const buildFindingsListEnrichedQueryFromBulkFilter = ({
  targetId,
  targetType,
  filter,
  skip,
  limit,
}: {
  targetId: string;
  targetType: TargetTypeType;
  filter?: FindingsBulkFilter;
  skip?: number;
  limit?: number;
}): FindingsListEnrichedQuery =>
  buildFindingsListEnrichedQueryForTarget({
    targetId,
    targetType,
    options: {
      skip,
      limit,
      endpointId: filter?.endpointId,
      searchTerm: filter?.searchTerm,
      severity: filter?.severity,
      status: filter?.status,
      category: filter?.category,
      isPublished: filter?.isPublished,
      wasAutoPublished: filter?.wasAutoPublished,
      hasPrevention: filter?.hasPrevention,
      hasPendingSeverityChangeRequest: filter?.hasPendingSeverityChangeRequest,
      createdStartDate: filter?.createdStartDate,
      createdEndDate: filter?.createdEndDate,
      discoveryStartDate: filter?.discoveryStartDate,
      discoveryEndDate: filter?.discoveryEndDate,
      lifecycleState: filter?.lifecycleState,
    },
  });
